import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App, Button, Form, Input, Modal, Select } from 'antd'
import { ArrowLeftOutlined, HomeOutlined, PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { api, type Edge } from '../api'
import { useSession } from '@argo/common/session'

/** The tile graph: folders open deeper, accounts open the account page. */
export default function Accounts() {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const navigate = useNavigate()
  const location = useLocation()
  const qc = useQueryClient()
  const { setAccount, me } = useSession()
  const operator = me?.roles.includes('operator') ?? false

  const target = location.pathname.startsWith('/g/') ? decodeURIComponent(location.pathname.slice(3)) : 'default'
  const [adding, setAdding] = useState(false)
  const [form] = Form.useForm<{ child: string; kind: string }>()

  const tiles = useQuery({ queryKey: ['graph', target], queryFn: () => api.graph(target) })
  const add = useMutation({
    mutationFn: (v: { child: string; kind: string }) => api.command('graph', 'add', target, v),
    onSuccess: () => { setAdding(false); form.resetFields(); qc.invalidateQueries({ queryKey: ['graph', target] }) },
    onError: (e: Error) => message.error(e.message),
  })

  const open = (edge: Edge) => {
    if (edge.kind === 'tuna') { setAccount(edge.child); navigate(`/a/${encodeURIComponent(edge.child)}`) }
    else navigate(`/g/${encodeURIComponent(target === 'default' ? edge.child : `${target}/${edge.child}`)}`)
  }
  const up = () => {
    const parts = target.split('/')
    parts.pop()
    navigate(parts.length ? `/g/${encodeURIComponent(parts.join('/'))}` : '/accounts')
  }

  return (
    <div className="page">
      <div className="panel">
        <div className="panel-head">
          <button className="icon-btn" onClick={() => navigate('/accounts')} disabled={target === 'default'} aria-label={t('home')}>
            <HomeOutlined />
          </button>
          <button className="icon-btn" onClick={up} disabled={target === 'default'} aria-label={t('back')}>
            <ArrowLeftOutlined />
          </button>
          <span className="grow mono">{target}</span>
          {operator && (
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setAdding(true)}>{t('add')}</Button>
          )}
        </div>

        <div className="panel-body">
          {tiles.isLoading && (
            <div className="tiles">
              {[0, 1, 2].map(i => <div key={i} className="skeleton" style={{ height: 104 }} />)}
            </div>
          )}
          {tiles.isError && <div className="empty">{(tiles.error as Error).message}</div>}
          {tiles.data && tiles.data.data.length === 0 && <div className="empty">{t('noData')}</div>}

          {tiles.data && tiles.data.data.length > 0 && (
            <div className="tiles">
              {tiles.data.data.map(edge => (
                <button
                  key={edge.child}
                  className={`tile ${edge.kind === 'tuna' || edge.kind === 'graph' ? edge.kind : 'other'}`}
                  onClick={() => open(edge)}
                >
                  <span className="kind">{edge.kind}</span>
                  <span>
                    <span className="name">{edge.child}</span>
                    <div className="value num">{edge.value || '—'}</div>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <Modal title={t('add')} open={adding} onCancel={() => setAdding(false)} onOk={() => form.submit()} confirmLoading={add.isPending}>
        <Form form={form} layout="vertical" initialValues={{ kind: 'tuna' }} onFinish={v => add.mutate(v)}>
          <Form.Item name="child" label={t('child')} rules={[{ required: true }]}><Input autoFocus /></Form.Item>
          <Form.Item name="kind" label={t('kind')} rules={[{ required: true }]}>
            <Select options={[
              { value: 'tuna', label: 'tuna — account' },
              { value: 'graph', label: 'graph — folder' },
              { value: 'system', label: 'system' },
            ]} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
